import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import api from '../../utils/api';
import AdminHeader from '../../components/AdminHeader';
import { getStatusConfig } from '../../utils/orderStatusConstants';
import './AdminCustomerDetail.css';

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return `฿${number.toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('th-TH');
};

const getStatusBadge = (status) => {
  const config = getStatusConfig(status);
  return (
    <span className="customer-status-badge" style={{ backgroundColor: config.color, color: config.textColor }}>
      {config.label}
    </span>
  );
};

export default function AdminCustomerDetail({ admin, logout, isSidebarCollapsed, toggleSidebar }) {
  const { customerId } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    let active = true;
    const fetchDetail = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await api.get(`/api/admin/customers/${customerId}`);
        if (active) {
          setData(response.data);
        }
      } catch (fetchError) {
        console.error('Error fetching customer detail:', fetchError);
        if (active) {
          setError('ไม่สามารถโหลดรายละเอียดลูกค้าได้');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      active = false;
    };
  }, [customerId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        // Focus search if exists
      } else if (e.key === 'E' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setActiveTab('notes');
        setTimeout(() => {
          const noteInput = document.querySelector('.customer-notes-input');
          if (noteInput) noteInput.focus();
        }, 100);
      } else if (e.key === 'T' && !e.ctrlKey && !e.metaKey && data?.customer?.phone) {
        e.preventDefault();
        window.location.href = `tel:${data.customer.phone.replace(/[^0-9+]/g, '')}`;
      } else if (e.key === 'M' && !e.ctrlKey && !e.metaKey && data?.customer?.email) {
        e.preventDefault();
        window.location.href = `mailto:${data.customer.email}`;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [data]);

  const handleViewOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data);
      setIsDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedOrder(null);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: Date.now(),
      text: newNote.trim(),
      author: admin?.name || 'Admin',
      createdAt: new Date().toISOString()
    };
    setNotes([note, ...notes]);
    setNewNote('');
    // TODO: Save to backend
  };

  const handleCopyEmail = () => {
    if (data?.customer?.email) {
      navigator.clipboard.writeText(data.customer.email);
    }
  };


  const orders = useMemo(() => data?.orders || [], [data?.orders]);
  const customer = data?.customer || {};

  const customerSummary = useMemo(() => {
    const totalSpent = orders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
    const ordersCount = orders.length;
    const awaitingPayment = orders.filter(
      (order) => (order.order_status || '').toLowerCase() === 'awaiting_payment'
    ).length;
    const installmentOrders = orders.filter((order) => {
      const method = (order.payment_method || '').toString().toLowerCase();
      return method.includes('install') || Number(order.installment_periods) > 1;
    });
    
    let installmentsPaid = 0;
    let installmentsTotal = 0;
    installmentOrders.forEach((order) => {
      if (order.installments) {
        installmentsTotal += order.installments.length;
        installmentsPaid += order.installments.filter((inst) => inst.payment_status === 'paid').length;
      } else {
        installmentsTotal += Number(order.installment_periods) || 0;
      }
    });

    return {
      totalSpent,
      ordersCount,
      awaitingPayment,
      installmentOrders: installmentOrders.length,
      installmentsPaid,
      installmentsTotal
    };
  }, [orders]);

  const topProducts = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      order.items?.forEach((item) => {
        if (!item || !item.product_id) return;
        const key = item.product_id;
        const existing = map.get(key) || {
          product_id: item.product_id,
          product_name: item.product_name,
          totalQuantity: 0, 
          totalSpent: 0,
          orderCount: 0
        };
        existing.totalQuantity += Number(item.quantity) || 0;
        existing.totalSpent += (Number(item.price) || 0) * (Number(item.quantity) || 0);
        existing.orderCount += 1;
        map.set(key, existing);
      });
    });
    return Array.from(map.values())
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .slice(0, 3);
  }, [orders]);

  const installmentDetails = useMemo(() => {
    return orders
      .filter((order) => {
        const method = (order.payment_method || '').toString().toLowerCase();
        return method.includes('install') || Number(order.installment_periods) > 1;
      })
      .map((order) => {
        const installments = order.installments || [];
        const paidCount = installments.filter((inst) => inst.payment_status === 'paid').length;
        const totalPeriods = Number(order.installment_periods) || installments.length;
        const nextDue = installments.find((inst) => inst.payment_status !== 'paid');
        
        return {
          orderId: order.order_id,
          orderDate: order.order_date,
          totalAmount: order.total_amount,
          monthlyPayment: order.monthly_payment,
          paidCount,
          totalPeriods,
          remainingPeriods: totalPeriods - paidCount,
          nextDue: nextDue?.payment_due_date,
          items: order.items || [],
          installments
        };
      });
  }, [orders]);

  const getPaymentMethodLabel = (order) => {
    if (!order) return '-';
    const method = (order.payment_method || '').toString().toLowerCase();
    if (method.includes('install')) return 'ผ่อนชำระ';
    if (method.includes('cash')) return 'ชำระเต็มจำนวน';
    return Number(order.installment_periods) > 1 ? 'ผ่อนชำระ' : 'ชำระเต็มจำนวน';
  };

  const renderAddress = (address) => {
    if (!address) return '-';
    const lines = [];
    const fullName = `${address.recipientName || ''} ${address.recipientSurname || ''}`.trim();
    if (fullName) lines.push(fullName);
    if (address.phone) lines.push(`โทร: ${address.phone}`);
    if (address.address) lines.push(address.address);
    return lines.length > 0 ? lines.join(' · ') : '-';
  };

  const customerInitial = customer.firstName
    ? customer.firstName.charAt(0).toUpperCase()
    : '?';

  if (isLoading) {
  return (
    <div className="admin-customer-detail-page">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />
        <div className="customer-detail__loading">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">กำลังโหลดข้อมูลลูกค้า...</p>
          </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-customer-detail-page">
        <AdminHeader
          admin={admin}
          onLogout={logout}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <Container>
          <Alert variant="danger">{error || 'ไม่พบข้อมูลลูกค้า'}</Alert>
          <Link to="/admin/customers">
            <Button variant="outline-secondary">← กลับสู่รายชื่อลูกค้า</Button>
          </Link>
        </Container>
        </div>
    );
  }

  return (
    <div className="admin-customer-detail-page">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />

      <Container fluid className="customer-detail__container">
        {/* Header Summary (Sticky) */}
        <section className="customer-detail__header">
          <div className="customer-detail__header-main">
            <div className="customer-detail__avatar-section">
              <div className="customer-detail__avatar">{customerInitial}</div>
              <div className="customer-detail__name-section">
                <h1 className="customer-detail__name">
                  {customer.firstName} {customer.lastName}
                </h1>
                <p className="customer-detail__meta">
                  รหัสลูกค้า #{customer.id} · {customer.username || 'ไม่ระบุ username'}
                </p>
          </div>
                    </div>

            {/* KPI Cards */}
            <div className="customer-detail__kpi-cards">
              <div className="customer-detail__kpi-card">
                <span className="customer-detail__kpi-label">ยอดใช้จ่ายรวม</span>
                <strong className="customer-detail__kpi-value">
                  {formatCurrency(customerSummary.totalSpent)}
                </strong>
                    </div>
              <div
                className={`customer-detail__kpi-card ${
                  customerSummary.awaitingPayment > 0 ? 'customer-detail__kpi-card--warn' : ''
                }`}
              >
                <span className="customer-detail__kpi-label">ออเดอร์ทั้งหมด / ค้างชำระ</span>
                <strong className="customer-detail__kpi-value">
                  {customerSummary.ordersCount} รายการ
                </strong>
                {customerSummary.awaitingPayment > 0 ? (
                  <span className="customer-detail__kpi-hint customer-detail__kpi-hint--warn">
                    ค้าง {customerSummary.awaitingPayment}
                  </span>
                ) : (
                  <span className="customer-detail__kpi-hint">ไม่มีค้าง</span>
                )}
                  </div>
              <div className="customer-detail__kpi-card">
                <span className="customer-detail__kpi-label">ออเดอร์แบบผ่อน</span>
                <strong className="customer-detail__kpi-value">
                  {customerSummary.installmentOrders} รายการ
                </strong>
                <span className="customer-detail__kpi-hint">
                  จ่ายแล้ว {customerSummary.installmentsPaid} งวด / {customerSummary.installmentsTotal}
                </span>
                      </div>
                      </div>
                      </div>

          {/* Quick Actions (Sticky Right) */}
          <div className="customer-detail__quick-actions">
            <Button
              variant="outline-primary"
              className="customer-detail__action-btn"
              onClick={() => {
                if (customer.phone) {
                  window.location.href = `tel:${customer.phone.replace(/[^0-9+]/g, '')}`;
                }
              }}
              title="กด T เพื่อโทร"
            >
              <i className="bi bi-telephone"></i>
              โทร
            </Button>
            <Button
              variant="outline-primary"
              className="customer-detail__action-btn"
              onClick={() => {
                if (customer.email) {
                  window.location.href = `mailto:${customer.email}`;
                }
              }}
              title="กด M เพื่อส่งเมล"
            >
              <i className="bi bi-envelope"></i>
              เมล
            </Button>
            <Button
              variant="outline-secondary"
              className="customer-detail__action-btn"
              onClick={() => {
                setActiveTab('notes');
                setTimeout(() => {
                  const noteInput = document.querySelector('.customer-notes-input');
                  if (noteInput) noteInput.focus();
                }, 100);
              }}
              title="กด E เพื่อเพิ่มโน้ต"
            >
              <i className="bi bi-sticky"></i>
              เพิ่มโน้ต
            </Button>
          </div>
            </section>

        {/* Contact Info & Top Products */}
        <section className="customer-detail__info-section">
          <Row className="g-4">
            <Col md={6}>
              <Card className="customer-detail__info-card">
                <div className="customer-detail__info-card-header">
                  <h3>ข้อมูลติดต่อ</h3>
                </div>
                <div className="customer-detail__info-card-body">
                  <div className="customer-detail__info-row">
                    <span className="customer-detail__info-label">อีเมล</span>
                    <div className="customer-detail__info-value">
                      {customer.email ? (
                        <button
                          className="customer-detail__copy-btn"
                          onClick={handleCopyEmail}
                          title="คลิกเพื่อคัดลอก"
                        >
                          {customer.email} <i className="bi bi-clipboard"></i>
                        </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                    </div>
                  </div>
                  <div className="customer-detail__info-row">
                    <span className="customer-detail__info-label">เบอร์โทร</span>
                    <div className="customer-detail__info-value">
                      {customer.phone ? (
                        <a
                          href={`tel:${customer.phone.replace(/[^0-9+]/g, '')}`}
                          className="customer-detail__phone-link"
                        >
                          {customer.phone}
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                    </div>
                  </div>
                  <div className="customer-detail__info-row">
                    <span className="customer-detail__info-label">ที่อยู่จัดส่งล่าสุด</span>
                    <div className="customer-detail__info-value">
                      {renderAddress(customer.address)}
                    </div>
                  </div>
                </div>
                </Card>
              </Col>
            <Col md={6}>
              <Card className="customer-detail__info-card">
                <div className="customer-detail__info-card-header">
                  <h3>สินค้าที่ซื้อบ่อย (Top 3)</h3>
                </div>
                <div className="customer-detail__info-card-body">
                  {topProducts.length === 0 ? (
                      <p className="text-muted mb-0">ยังไม่มีประวัติการซื้อสินค้า</p>
                    ) : (
                    <div className="customer-detail__top-products">
                      {topProducts.map((product, index) => (
                        <div key={product.product_id || index} className="customer-detail__top-product-item">
                          <div className="customer-detail__top-product-rank">{index + 1}.</div>
                          <div className="customer-detail__top-product-info">
                            <div className="customer-detail__top-product-name">{product.product_name}</div>
                            <div className="customer-detail__top-product-meta">
                              {product.totalQuantity} ชิ้น · {formatCurrency(product.totalSpent)}
                          </div>
                                    </div>
                                  </div>
                            ))}
                      </div>
                    )}
                </div>
                </Card>
              </Col>
            </Row>
        </section>

        {/* Tabs */}
        <section className="customer-detail__tabs-section">
          <div className="customer-detail__tabs-header">
            <button
              className={`customer-detail__tab ${activeTab === 'orders' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              ออเดอร์
            </button>
            <button
              className={`customer-detail__tab ${activeTab === 'installments' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('installments')}
            >
              ผ่อนชำระ
            </button>
            <button
              className={`customer-detail__tab ${activeTab === 'top-products' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('top-products')}
            >
              สินค้าที่ซื้อบ่อย
            </button>
            <button
              className={`customer-detail__tab ${activeTab === 'notes' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              โน้ต
            </button>
                  </div>

          <div className="customer-detail__tabs-content">
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <Card className="customer-detail__tab-card">
                <div className="customer-detail__tab-card-body">
                  {orders.length === 0 ? (
                    <div className="customer-detail__empty-state">
                      <i className="bi bi-inbox"></i>
                      <strong>ยังไม่มีคำสั่งซื้อ</strong>
                      <p>เมื่อลูกค้ามีคำสั่งซื้อ จะแสดงที่นี่</p>
                </div>
                  ) : (
                    <Table responsive hover className="customer-detail__orders-table">
                      <thead>
                        <tr>
                          <th>รหัสสั่งซื้อ</th>
                          <th>วันที่</th>
                          <th>สถานะ</th>
                          <th>การชำระเงิน</th>
                          <th>ยอดรวม</th>
                          <th>สินค้า</th>
                          <th className="text-end">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.order_id}>
                            <td className="fw-semibold">#{order.order_id}</td>
                            <td>{formatDate(order.order_date)}</td>
                            <td>{getStatusBadge(order.order_status)}</td>
                            <td>{getPaymentMethodLabel(order)}</td>
                            <td className="customer-detail__amount">{formatCurrency(order.total_amount)}</td>
                            <td>
                              <div className="customer-detail__order-items">
                                {order.items?.slice(0, 2).map((item) => (
                                  <span key={item.order_detail_id} className="customer-detail__order-item">
                                    {item.product_name} × {item.quantity}
                                  </span>
                                ))}
                                {order.items?.length > 2 && (
                                  <span className="customer-detail__order-item-more">
                                    +{order.items.length - 2} รายการ
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewOrderDetails(order.order_id)}
                              >
                                <i className="bi bi-eye me-1"></i>
                                ดูรายละเอียด
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card>
            )}

            {/* Installments Tab */}
            {activeTab === 'installments' && (
              <div className="customer-detail__installments-grid">
                {installmentDetails.length === 0 ? (
                  <Card className="customer-detail__tab-card">
                    <div className="customer-detail__tab-card-body">
                      <div className="customer-detail__empty-state">
                        <i className="bi bi-calendar-x"></i>
                        <strong>ยังไม่มีการสั่งซื้อแบบผ่อนชำระ</strong>
                        <p>เมื่อลูกค้ามีออเดอร์แบบผ่อน จะแสดงที่นี่</p>
              </div>
                    </div>
                </Card>
              ) : (
                  installmentDetails.map((installment) => {
                    const progress = installment.totalPeriods > 0
                      ? Math.round((installment.paidCount / installment.totalPeriods) * 100)
                      : 0;
                    const nextDueDate = installment.nextDue ? new Date(installment.nextDue) : null;
                    const isOverdue = nextDueDate && nextDueDate < new Date();
                    
                    return (
                      <Card key={installment.orderId} className="customer-detail__installment-card">
                        <div className="customer-detail__installment-header">
                            <div>
                            <h4>คำสั่งซื้อ #{installment.orderId}</h4>
                            <span className="customer-detail__installment-date">
                              {formatDate(installment.orderDate)}
                            </span>
                            </div>
                          <Badge
                            bg={progress === 100 ? 'success' : 'warning'}
                            className="customer-detail__installment-badge"
                          >
                            {installment.paidCount}/{installment.totalPeriods} งวด
                            </Badge>
                          </div>
                        <div className="customer-detail__installment-progress">
                          <div
                            className="customer-detail__installment-progress-bar"
                            style={{ width: `${progress}%` }}
                          />
                          </div>
                        <div className="customer-detail__installment-info">
                          <div className="customer-detail__installment-info-row">
                            <span>ยอดต่อเดือน:</span>
                            <strong>{formatCurrency(installment.monthlyPayment)}</strong>
                          </div>
                          <div className="customer-detail__installment-info-row">
                            <span>ยอดรวม:</span>
                            <strong>{formatCurrency(installment.totalAmount)}</strong>
                </div>
                          <div className="customer-detail__installment-info-row">
                            <span>งวดที่จ่ายแล้ว:</span>
                            <strong>{installment.paidCount} งวด</strong>
                </div>
                          <div className="customer-detail__installment-info-row">
                            <span>งวดคงเหลือ:</span>
                            <strong>{installment.remainingPeriods} งวด</strong>
                </div>
                          <div className={`customer-detail__installment-info-row ${
                            isOverdue ? 'customer-detail__installment-info-row--overdue' : ''
                          }`}>
                            <span>กำหนดชำระถัดไป:</span>
                            <strong>
                              {installment.nextDue ? formatDate(installment.nextDue) : '-'}
                            </strong>
                </div>
                </div>
                        <div className="customer-detail__installment-items">
                          <span className="customer-detail__installment-items-label">สินค้าในคำสั่งซื้อ:</span>
                          <div className="customer-detail__installment-items-list">
                            {installment.items.map((item) => (
                              <span key={item.order_detail_id}>
                                {item.product_name} × {item.quantity}
                              </span>
                            ))}
                  </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Top Products Tab */}
            {activeTab === 'top-products' && (
              <Card className="customer-detail__tab-card">
                <div className="customer-detail__tab-card-body">
                  {topProducts.length === 0 ? (
                    <div className="customer-detail__empty-state">
                      <i className="bi bi-box-seam"></i>
                      <strong>ยังไม่มีประวัติการซื้อสินค้า</strong>
                      <p>เมื่อลูกค้าซื้อสินค้า จะแสดงรายการที่นี่</p>
              </div>
                  ) : (
                    <Table responsive hover className="customer-detail__products-table">
                  <thead>
                    <tr>
                          <th>#</th>
                      <th>สินค้า</th>
                          <th className="text-center">จำนวนรวม</th>
                          <th className="text-center">จำนวนออเดอร์</th>
                          <th className="text-end">ยอดใช้จ่ายรวม</th>
                    </tr>
                  </thead>
                  <tbody>
                        {topProducts.map((product, index) => (
                          <tr key={product.product_id || index}>
                            <td className="text-center">
                              <span className="customer-detail__product-rank">{index + 1}</span>
                            </td>
                            <td>
                              <strong>{product.product_name}</strong>
                              {index === 0 && (
                                <Badge bg="warning" className="ms-2">
                                  TOP SELLER
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">{product.totalQuantity} ชิ้น</td>
                            <td className="text-center">{product.orderCount} ออเดอร์</td>
                            <td className="text-end">{formatCurrency(product.totalSpent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                  )}
              </div>
              </Card>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <Card className="customer-detail__tab-card">
                <div className="customer-detail__tab-card-body">
                  <div className="customer-detail__notes-input-section">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="เพิ่มโน้ตสำหรับลูกค้านี้... (กด Enter เพื่อบันทึก)"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          handleAddNote();
                        }
                      }}
                      className="customer-notes-input"
                    />
                    <Button
                      variant="primary"
                      className="customer-detail__notes-add-btn"
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      เพิ่มโน้ต
                    </Button>
                  </div>
                  <div className="customer-detail__notes-timeline">
                    {notes.length === 0 ? (
                      <div className="customer-detail__empty-state">
                        <i className="bi bi-sticky"></i>
                        <strong>ยังไม่มีโน้ต</strong>
                        <p>เพิ่มโน้ตเพื่อบันทึกข้อมูลสำคัญเกี่ยวกับลูกค้า</p>
                      </div>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="customer-detail__note-item">
                          <div className="customer-detail__note-header">
                            <strong>{note.author}</strong>
                            <span className="customer-detail__note-date">
                              {formatDateTime(note.createdAt)}
                            </span>
                          </div>
                          <div className="customer-detail__note-text">{note.text}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </section>
      </Container>

      {/* Order Drawer */}
      <div className={`customer-detail__drawer ${isDrawerOpen ? 'is-open' : ''}`}>
        <div className="customer-detail__drawer-header">
          <h3>
            <i className="bi bi-receipt"></i>
            รายละเอียดคำสั่งซื้อ {selectedOrder ? `#${selectedOrder.order_id}` : ''}
          </h3>
          <button
            className="customer-detail__drawer-close"
            onClick={handleCloseDrawer}
            aria-label="ปิด"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        {selectedOrder && (
          <div className="customer-detail__drawer-body">
            <div className="customer-detail__drawer-section">
              <h4>
                <i className="bi bi-box-seam"></i>
                รายการสินค้า
              </h4>
              <div className="customer-detail__drawer-items">
                {selectedOrder.details && selectedOrder.details.length > 0 ? (
                  selectedOrder.details.map((item) => (
                    <div key={item.order_detail_id} className="customer-detail__drawer-item">
                      {item.product_image && (
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="customer-detail__drawer-item-image"
                        />
                      )}
                      <div className="customer-detail__drawer-item-info">
                        <div className="customer-detail__drawer-item-name">{item.product_name}</div>
                        <div className="customer-detail__drawer-item-meta">
                          <span>x{item.quantity}</span>
                          <strong>{formatCurrency(item.price)}</strong>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">ไม่มีรายการสินค้า</p>
                )}
              </div>
            </div>

            <div className="customer-detail__drawer-section">
              <h4>
                <i className="bi bi-geo-alt"></i>
                ที่อยู่จัดส่ง
              </h4>
              <div className="customer-detail__drawer-content">
                <pre className="customer-detail__drawer-address">
                  {renderAddress(selectedOrder.shipping_address || customer.address)}
                </pre>
              </div>
            </div>

            <div className="customer-detail__drawer-section">
              <h4>
                <i className="bi bi-currency-dollar"></i>
                สรุปยอด
              </h4>
              <div className="customer-detail__drawer-content">
                <div className="customer-detail__drawer-row">
                  <span>ยอดรวม:</span>
                  <strong className="customer-detail__drawer-total">
                    {formatCurrency(selectedOrder.total_amount)}
                  </strong>
                </div>
                <div className="customer-detail__drawer-row">
                  <span>วิธีชำระ:</span>
                  <strong>{getPaymentMethodLabel(selectedOrder)}</strong>
                </div>
                <div className="customer-detail__drawer-row">
                  <span>สถานะ:</span>
                  {getStatusBadge(selectedOrder.order_status)}
                </div>
              </div>
            </div>

            {selectedOrder.installments && selectedOrder.installments.length > 0 && (
              <div className="customer-detail__drawer-section">
                <h4>
                  <i className="bi bi-calendar-check"></i>
                  ตารางผ่อนชำระ
                </h4>
                <div className="customer-detail__drawer-content">
                  <Table bordered hover size="sm" className="customer-detail__drawer-installments-table">
                      <thead>
                        <tr>
                          <th>งวด</th>
                          <th>ยอดชำระ</th>
                          <th>กำหนดชำระ</th>
                          <th>วันที่ชำระ</th>
                          <th>สถานะ</th>
                        </tr>
                      </thead>
                      <tbody>
                      {selectedOrder.installments.map((installment) => {
                        const dueDate = installment.payment_due_date
                          ? new Date(installment.payment_due_date)
                          : null;
                        const isOverdue = dueDate && dueDate < new Date() && installment.payment_status !== 'paid';
                        
                        return (
                          <tr key={installment.installment_id} className={isOverdue ? 'table-danger' : ''}>
                            <td>{installment.installment_number}</td>
                            <td>{formatCurrency(installment.installment_amount)}</td>
                            <td className={isOverdue ? 'text-danger fw-bold' : ''}>
                              {formatDate(installment.payment_due_date)}
                            </td>
                            <td>{formatDate(installment.payment_date) || '-'}</td>
                            <td>
                              <Badge bg={installment.payment_status === 'paid' ? 'success' : 'secondary'}>
                                {installment.payment_status === 'paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </Table>
                </div>
              </div>
            )}

                </div>
              )}
      </div>

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div className="customer-detail__drawer-overlay" onClick={handleCloseDrawer}></div>
      )}

      {/* Back Button */}
      <div className="customer-detail__back-btn-wrapper">
        <Link to="/admin/customers">
          <Button variant="outline-secondary" className="customer-detail__back-btn">
            <i className="bi bi-arrow-left me-2"></i>
            กลับสู่รายชื่อลูกค้า
          </Button>
        </Link>
      </div>
    </div>
  );
}
